import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Plus, Trash, Handshake, Target, Briefcase } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'partner_name', label: 'Partner Names', icon: Handshake, placeholder: 'e.g. TalentBridge Consulting' },
  { key: 'role', label: 'Roles', icon: Target, placeholder: 'e.g. Senior Backend Engineer' },
  { key: 'engagement_type', label: 'Engagement Types', icon: Briefcase, placeholder: 'e.g. Permanent' },
];

const MasterDataManagement = () => {
  const [activeCategory, setActiveCategory] = useState('partner_name');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/master-data`);
      setItems(data.filter((i) => i.category === activeCategory));
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const addItem = async () => {
    if (!newValue.trim()) {
      toast.error('Enter a value');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/master-data`, { category: activeCategory, value: newValue.trim() });
      toast.success('Added');
      setNewValue('');
      fetchItems();
    } catch (error) {
      console.error('Error adding master data:', error);
      toast.error(error.response?.data?.detail || 'Failed to add value');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this value?')) return;
    try {
      await axios.delete(`${API}/master-data/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (error) {
      console.error('Error deleting master data:', error);
      toast.error('Failed to delete value');
    }
  };

  const activeMeta = CATEGORIES.find((c) => c.key === activeCategory);

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="master-data-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Master Data
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Manage the lists used across the app, like partner names, roles, and engagement types
          </p>
        </div>

        <div className="inline-flex bg-slate-100 p-1 rounded-lg mb-6" data-testid="master-data-tabs">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center space-x-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${
                  activeCategory === cat.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`master-data-tab-${cat.key}`}
              >
                <Icon size={16} weight="regular" />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-5">
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value.slice(0, 255))}
              onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
              placeholder={activeMeta?.placeholder}
              className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="master-data-new-value-input"
            />
            <button
              onClick={addItem}
              disabled={submitting}
              className="flex items-center space-x-2 btn-primary px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"
              data-testid="master-data-add-button"
            >
              <Plus size={18} weight="bold" />
              <span>Add</span>
            </button>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-11 bg-slate-100 rounded-md skeleton" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10" data-testid="master-data-empty-state">
              <Database size={40} weight="duotone" className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No {activeMeta?.label.toLowerCase()} added yet</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="master-data-list">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between px-4 py-2.5 border border-slate-200 rounded-md"
                  data-testid={`master-data-item-${item.id}`}
                >
                  <span className="text-sm text-slate-800">{item.value}</span>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    data-testid={`master-data-delete-${item.id}`}
                    title="Delete"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterDataManagement;
